import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Umbrella } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { employeesAPI, policiesAPI, termCoveragesAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { filterBySearch } from '../utils/search';

export default function TermCoverages() {
  const [coverages, setCoverages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [termPolicies, setTermPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [premiumPreview, setPremiumPreview] = useState(null);
  const [form, setForm] = useState({
    policy_id: '',
    employee_id: '',
    enrollment_date: new Date().toISOString().slice(0, 10),
    sum_assured: '',
    premium_mode: 'annual',
    nominee_name: '',
    nominee_relation: 'spouse',
    nominee_date_of_birth: '',
    nominee_contact: '',
  });
  const [search, setSearch] = useState('');

  const filteredCoverages = useMemo(
    () => filterBySearch(coverages, search, [
      'employee_name',
      'employee_code',
      'policy_number',
      'nominee_name',
      'nominee_relation',
      'provider_name',
    ]),
    [coverages, search]
  );

  const load = () => {
    termCoveragesAPI.list()
      .then(({ data }) => setCoverages(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    employeesAPI.list().then(({ data }) => setEmployees(data));
    policiesAPI.list().then(({ data }) => {
      setTermPolicies(data.filter((p) => p.policy_type === 'term_life'));
    });
  }, []);

  useEffect(() => {
    if (!showForm || !form.policy_id || !form.employee_id) {
      setPremiumPreview(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await termCoveragesAPI.calculatePremium({
          policy_id: parseInt(form.policy_id, 10),
          employee_id: parseInt(form.employee_id, 10),
          enrollment_date: form.enrollment_date,
        });
        setPremiumPreview(data.premium);
      } catch {
        setPremiumPreview(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [showForm, form.policy_id, form.employee_id, form.enrollment_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await termCoveragesAPI.create({
        ...form,
        policy_id: parseInt(form.policy_id, 10),
        employee_id: parseInt(form.employee_id, 10),
        sum_assured: form.sum_assured ? parseFloat(form.sum_assured) : undefined,
      });
      setShowForm(false);
      setForm({
        policy_id: '', employee_id: '', enrollment_date: new Date().toISOString().slice(0, 10),
        sum_assured: '', premium_mode: 'annual', nominee_name: '', nominee_relation: 'spouse',
        nominee_date_of_birth: '', nominee_contact: '',
      });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to enroll term coverage');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Term Insurance Coverages</h1>
          <p className="text-slate-500 mt-1">Enroll employees in term life policies with age-based premium</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2 self-start">
          <Plus className="w-4 h-4" />
          Enroll Term Coverage
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-semibold text-lg">New Term Enrollment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label">Term Policy *</label>
              <select className="input-field" required value={form.policy_id} onChange={(e) => setForm({ ...form, policy_id: e.target.value })}>
                <option value="">Select</option>
                {termPolicies.map((p) => (
                  <option key={p.id} value={p.id}>{p.policy_number} — {p.company_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Employee *</label>
              <select className="input-field" required value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                <option value="">Select</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Enrollment Date</label>
              <input type="date" className="input-field" value={form.enrollment_date} onChange={(e) => setForm({ ...form, enrollment_date: e.target.value })} />
            </div>
            <div>
              <label className="label">Sum Assured (INR)</label>
              <input type="number" className="input-field" placeholder="Uses policy default if empty" value={form.sum_assured} onChange={(e) => setForm({ ...form, sum_assured: e.target.value })} />
            </div>
            <div>
              <label className="label">Premium Mode</label>
              <select className="input-field" value={form.premium_mode} onChange={(e) => setForm({ ...form, premium_mode: e.target.value })}>
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="label">Nominee Name</label>
              <input className="input-field" value={form.nominee_name} onChange={(e) => setForm({ ...form, nominee_name: e.target.value })} />
            </div>
            <div>
              <label className="label">Nominee Relation</label>
              <input className="input-field" value={form.nominee_relation} onChange={(e) => setForm({ ...form, nominee_relation: e.target.value })} />
            </div>
            <div>
              <label className="label">Nominee DOB</label>
              <input type="date" className="input-field" value={form.nominee_date_of_birth} onChange={(e) => setForm({ ...form, nominee_date_of_birth: e.target.value })} />
            </div>
            <div>
              <label className="label">Nominee Contact</label>
              <input className="input-field" value={form.nominee_contact} onChange={(e) => setForm({ ...form, nominee_contact: e.target.value })} />
            </div>
          </div>

          {premiumPreview && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-lg text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>Age: <strong>{premiumPreview.age}</strong></div>
              <div>Slab: <strong>{premiumPreview.slabInfo?.ageBand}</strong></div>
              <div>Annual: <strong>{formatCurrency(premiumPreview.annualPremium)}</strong></div>
              <div>Total: <strong>{formatCurrency(premiumPreview.totalPremium)}</strong></div>
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Enroll & Calculate Premium</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <SearchInput
        className="max-w-md"
        placeholder="Search by employee, policy, nominee…"
        value={search}
        onChange={setSearch}
      />

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="pb-3 pr-4 font-medium">Employee</th>
              <th className="pb-3 pr-4 font-medium">Policy</th>
              <th className="pb-3 pr-4 font-medium">Sum Assured</th>
              <th className="pb-3 pr-4 font-medium">Premium</th>
              <th className="pb-3 pr-4 font-medium">Nominee</th>
              <th className="pb-3 font-medium">Provider</th>
            </tr>
          </thead>
          <tbody>
            {filteredCoverages.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <Umbrella className="w-4 h-4 text-rose-500" />
                    <div>
                      <Link to={`/employees/${c.employee_id}`} className="font-medium text-primary-600 hover:underline">{c.employee_name}</Link>
                      <div className="text-xs text-slate-400">{c.employee_code} · Age {c.age}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 text-slate-600">{c.policy_number}</td>
                <td className="py-3 pr-4 font-medium">{formatCurrency(c.sum_assured)}</td>
                <td className="py-3 pr-4">{formatCurrency(c.total_premium)}</td>
                <td className="py-3 pr-4 capitalize">{c.nominee_name || '—'} {c.nominee_relation ? `(${c.nominee_relation})` : ''}</td>
                <td className="py-3 text-slate-500">{c.provider_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {coverages.length === 0 && (
          <p className="text-center py-8 text-slate-500">No term coverages enrolled yet.</p>
        )}
        {coverages.length > 0 && filteredCoverages.length === 0 && (
          <p className="text-center py-8 text-slate-500">No coverages match your search.</p>
        )}
      </div>
    </div>
  );
}
